/**
 * headerRailTiers — which header rail the chat pane wears, decided from the
 * PANE'S MEASURED WIDTH (owner screenshots, Miles 2026-09-15).
 *
 * The finding. A foldable phone in landscape (~1000 CSS px) opens the chat
 * fullscreen with the 264px thread sidebar, which leaves the header ≈736px —
 * and it wore the FULL eight-button rail: bell, pin, folder, archive,
 * add-people, @, mode-cycle, minimize. On touch each of those is 44px
 * (HeaderIconButton's `hover: none` tier), so the rail alone was 8×44 + 7×6
 * = 394px; with the two 24px insets and the 12px gap that left 258px for the
 * title column. The title guillotined to "Appointmen…", "6 people" broke to
 * two lines and the summon pill was cut to "Pr…". The only thing that ever
 * picked the compact rail was `dockedPanelDensity || isMobileViewport` — the
 * docked flag, or the 820px VIEWPORT query — never the width the header
 * actually had. The floating desktop pane between 821 and ~955px viewports
 * (`min(900px, 92vw)`) had the same problem.
 *
 * The rule is pure and measured, never a second viewport breakpoint:
 *   • docked panel or phone      → 'dense'   (today's 3-button rail, unchanged)
 *   • unmeasured (null)          → 'wide'    (today's first paint; the observer
 *                                            corrects it before the next frame)
 *   • pane < COMPACT_MIN (560)   → 'dense'   (even five buttons crowd a title)
 *   • pane < WIDE_MIN (860)      → 'compact' (bell + @ stay inline — they carry
 *                                            LIVE state; housekeeping folds into ⋯)
 *   • otherwise                  → 'wide'    (byte-identical to today)
 *
 * Why 860: 394 (touch rail) + 72 (insets) + 12 (gap) + 260 (the title's own
 * desktop cap) = 738 is the bare minimum for the wide rail to leave the title
 * whole; the subtitle row (people count + pill, ≈120px more) wants the room
 * too, and the desktop floating pane measures 898 (900 minus its border), so
 * 860 keeps that pane wide with margin while the foldable (≈736) lands
 * compact. Why 560: the compact rail is 5×44 + 4×6 = 244; under 560 the title
 * column falls back below the 260 it had in the docked pane at 504 — and the
 * docked pane already wears the dense rail by its own flag.
 *
 * The width measured is the ChatContainer's CONTENT box — exactly what the
 * `@container chat-pane` queries in styles.ts see, so the CSS half of this
 * round (the subtitle row wrapping, the two-line title) keys off the SAME
 * number through `HEADER_RAIL_WIDE_MIN_PX`. A pure leaf on purpose: no
 * imports, so styles.ts and the host can both read it without a cycle.
 */

export type HeaderRailTier = 'wide' | 'compact' | 'dense';

/** Below this pane width the header wears the docked-panel rail ([⋯][mode][hide]). */
export const HEADER_RAIL_COMPACT_MIN_PX = 560;
/** At or above this pane width the header wears today's full wide rail. */
export const HEADER_RAIL_WIDE_MIN_PX = 860;

export interface HeaderRailTierInput {
  /** The chat is the docked right panel (dockedDensity.isDockedPanelMode). */
  docked: boolean;
  /** Phone-sized viewport (useGlobalChat's isMobileViewport). */
  phone: boolean;
  /** ChatContainer content-box width in CSS px; null when not measured (yet). */
  paneWidthPx: number | null;
}

export function headerRailTier(input: HeaderRailTierInput): HeaderRailTier {
  if (input.docked || input.phone) return 'dense';
  const w = input.paneWidthPx;
  if (w === null || !Number.isFinite(w)) return 'wide';
  if (w < HEADER_RAIL_COMPACT_MIN_PX) return 'dense';
  if (w < HEADER_RAIL_WIDE_MIN_PX) return 'compact';
  return 'wide';
}
